import { Description, ErrorMessage, Field, FieldGroup, Label } from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { Select } from '@oysterjs/ui/select';
import { AddressAutofill as _AddressAutofill } from '@mapbox/search-js-react';
import React from 'react';
// @mapbox/search-js-react's ForwardRefExoticComponent<...<unknown>> is not compatible
// with TypeScript 6's stricter JSX component signature check. Cast to FC to unblock.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const AddressAutofill = _AddressAutofill as unknown as React.FC<any>;
import { getMapboxPublicKey } from '@oysterjs/core/config';
import { states } from '@oysterjs/core/states';
import { TransitionDiv } from './async';

interface Address {
  line1: string;
  line2?: string | null;
  city: string;
  zone: string;
  postalCode: string;
}

export const AddressForm = (props: {
  onChange: (update: (prev: Address) => Address) => void;
  disabled?: boolean;
  addressTitle: string;
  addressDescription?: string;
  initialValue: Address;
  validationErrors?: {
    line1?: string;
    line2?: string;
    city?: string;
    zone?: string;
    postalCode?: string;
  };
}) => {
  const onRetrieve = (res) => {
    const address = res?.features?.[0]?.properties;
    if (address) {
      props.onChange(() => ({
        line1: address.address_line1,
        line2: address.address_line2 || undefined,
        city: address.address_level2,
        zone: address.address_level1,
        postalCode: address.postcode
      }));
    }
  };

  const showSecondLine = !!props.initialValue.line2 || !!props.validationErrors?.line2;
  const showCityStateZip =
    !!props.initialValue.line1 ||
    !!props.initialValue.city ||
    !!props.initialValue.zone ||
    !!props.initialValue.postalCode ||
    !!props.validationErrors?.city ||
    !!props.validationErrors?.zone ||
    !!props.validationErrors?.postalCode;

  return (
    <>
      <FieldGroup>
        <Field>
          <Label>{props.addressTitle}</Label>
          {props.addressDescription && <Description>{props.addressDescription}</Description>}
          <div data-slot="control">
            <AddressAutofill
              accessToken={getMapboxPublicKey()}
              options={{ country: 'us' }}
              onRetrieve={onRetrieve}
            >
              <Input
                type="text"
                placeholder="276 Fifth Ave"
                autoComplete="address-line1"
                disabled={props.disabled}
                value={props.initialValue.line1}
                invalid={!!props.validationErrors?.line1}
                onChange={(e) => {
                  const val = e.currentTarget.value;
                  props.onChange((prev) => ({ ...prev, line1: val }));
                }}
              />
            </AddressAutofill>
          </div>
          {props.validationErrors?.line1 && (
            <ErrorMessage>{props.validationErrors?.line1}</ErrorMessage>
          )}
        </Field>
      </FieldGroup>
      {showSecondLine && (
        <TransitionDiv>
          <FieldGroup className="pt-6">
            <Field>
              <Input
                type="text"
                placeholder="Suite 100"
                autoComplete="address-line2"
                disabled={props.disabled}
                value={props.initialValue.line2 || ''}
                invalid={!!props.validationErrors?.line2}
                onChange={(e) => {
                  const val = e.currentTarget.value;
                  props.onChange((prev) => ({ ...prev, line2: val }));
                }}
              />
              {props.validationErrors?.line2 && (
                <ErrorMessage>{props.validationErrors?.line2}</ErrorMessage>
              )}
            </Field>
          </FieldGroup>
        </TransitionDiv>
      )}
      {showCityStateZip && (
        <TransitionDiv>
          <FieldGroup className="flex flex-col gap-x-4 xs:flex-row">
            <Field className="w-full xs:w-[45%] pt-6">
              <Label>City</Label>
              <Input
                type="text"
                placeholder="New York"
                autoComplete="address-level2"
                disabled={props.disabled}
                value={props.initialValue.city}
                invalid={!!props.validationErrors?.city}
                onChange={(e) => {
                  const val = e.currentTarget.value;
                  props.onChange((prev) => ({ ...prev, city: val }));
                }}
              />
              {props.validationErrors?.city && (
                <ErrorMessage>{props.validationErrors?.city}</ErrorMessage>
              )}
            </Field>
            <Field className="w-full xs:w-[35%]">
              <Label>State</Label>
              <Select
                autoComplete="address-level1"
                disabled={props.disabled}
                value={props.initialValue.zone}
                invalid={!!props.validationErrors?.zone}
                onChange={(e) => {
                  const val = e.currentTarget.value;
                  props.onChange((prev) => ({ ...prev, zone: val }));
                }}
              >
                <option value="" disabled></option>
                {states.map((state) => (
                  <option key={state.value} value={state.value}>
                    {state.name}
                  </option>
                ))}
              </Select>
              {props.validationErrors?.zone && (
                <ErrorMessage>{props.validationErrors?.zone}</ErrorMessage>
              )}
            </Field>
            <Field className="w-1/2 xs:w-[20%]">
              <Label>Zip Code</Label>
              <Input
                type="text"
                placeholder="10001"
                autoComplete="postal-code"
                disabled={props.disabled}
                value={props.initialValue.postalCode}
                invalid={!!props.validationErrors?.postalCode}
                onChange={(e) => {
                  const val = e.currentTarget.value;
                  props.onChange((prev) => ({ ...prev, postalCode: val }));
                }}
              />
              {props.validationErrors?.postalCode && (
                <ErrorMessage>{props.validationErrors?.postalCode}</ErrorMessage>
              )}
            </Field>
          </FieldGroup>
        </TransitionDiv>
      )}
    </>
  );
};
